import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps, useLocation } from '@reach/router';
import { RootState } from '@/redux/reducers';
import { getSubdomain, getRouteForPath, LightenDarkenColor } from '@/utils';
import LandingHeader from '@/containers/LandingHeader';
import LandingNavbar from '@/containers/LandingNavbar';
import EntitySocialVote from '@/containers/EntitySocialVote';
import BankApiFooter from '@/containers/BankApiFooter';
import Authorized from '@/components/Authorized';
import Unauthorized from '@/pages/Unauthorized';
import appRoutes from '@/config/routes';
import { useConfigLayout } from '../ConfigureLayout/ConfigureLayout';
import { navMenus } from './data';
import './EntityLayout.less';

interface EntityLayoutProps extends RouteComponentProps {
  children: React.ReactNode;
}

const EntityLayout: React.FC<EntityLayoutProps> = ({ children }) => {
  const dispatch = useDispatch();
  const { organisation } = useSelector((state: RootState) => state.openBankingDiretory);
  const errors: any = useSelector((state: RootState) => state.errors);
  const { authUser } = useConfigLayout();
  const subdomain = getSubdomain();
  const entityHeaderEl = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();
  const [defaultMenu, setDefaultMenu] = useState<{ label: string; url: string }>({
    label: '',
    url: '',
  });
  const route = getRouteForPath(window.location.pathname, appRoutes);

  useEffect(() => {
    if (organisation) {
      document.documentElement.style.setProperty('--themePrimaryColor', organisation.primaryColor);
      document.documentElement.style.setProperty('--themeSecondaryColor', organisation.accentColor);
      document.documentElement.style.setProperty(
        '--themePrimaryColorLighten',
        LightenDarkenColor(organisation.primaryColor, 30),
      );
      document.documentElement.style.setProperty(
        '--themeSecondaryColorLighten',
        LightenDarkenColor(organisation.accentColor, 30),
      );
      document.documentElement.style.setProperty(
        '--themePrimaryColorDarken',
        LightenDarkenColor(organisation.primaryColor, -30),
      );
      document.documentElement.style.setProperty(
        '--themeSecondaryColorDarken',
        LightenDarkenColor(organisation.accentColor, -30),
      );
    }
    // eslint-disable-next-line
  }, [dispatch, subdomain, organisation]);

  useEffect(() => {
    const defaultNavMenu = navMenus.find((menu) => menu.url === pathname);
    defaultNavMenu && setDefaultMenu(defaultNavMenu);
  }, [pathname]);

  return (
    <div className="EntityLayout">
      <div className="EntityLayout-header" ref={entityHeaderEl}>
        <LandingHeader />
      </div>
      <div className="EntityLayout-navbar">
        <LandingNavbar
          navMenus={navMenus}
          logo={organisation?.logo}
          defaultMenu={defaultMenu}
          showHeaderName={organisation?.showHeaderName}
          bankName={organisation?.displayName}
        />
      </div>
      {!errors.FETCH_ENTITY && (
        <div className="EntityLayout-summary">
          <EntitySocialVote />
        </div>
      )}
      <div className="EntityLayout-body">
        <Authorized
          authUser={authUser}
          route={route}
          organisationId={organisation?.id}
          unauthorizedPage={<Unauthorized />}
        >
          {children}
        </Authorized>
      </div>
      <div className="EntityLayout-footer">
        <BankApiFooter bankName={organisation?.displayName} />
      </div>
    </div>
  );
};

export default EntityLayout;
